import React, { useState } from "react";
import { css } from "@emotion/css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getUserKey, postData } from "../../utils/auth";
import { config } from "../../config";
import Loader from "../../common/loader/Loader";
import { displayRazorpayPaymentSdk } from "../../utils/payment";
import WhatsappJoinDialog from "./WhatsappJoinDialog";
import RegistrationPaymentDialog from "./RegistrationPaymentDialog";

const actionButtonStyle = (bgColor, hoverColor) => css`
    width: 100%;
    background-color: ${bgColor};
    color: white;
    font-size: 14px;
    font-weight: 600;
    padding: 10px 24px;
    border-radius: 6px;
    cursor: pointer;
    border: none;
    transition: background-color 0.2s, opacity 0.2s;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    @media (min-width: 768px) {
        width: auto;
        min-width: 140px;
    }
    &:hover:not(:disabled) {
        background-color: ${hoverColor};
    }
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;


const EventActionButton = ({ event, fetchEvents, isSubscribed }) => {
    const userData = getUserKey();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [paymentUpdateResponse, setPaymentUpdateResponse] = useState(null);
    const [registrationPaymentStatus, setRegistrationPaymentStatus] = useState("");

    const hideWhatsappDialog = () => {
        setPaymentUpdateResponse(null);
        fetchEvents();
    };

    const sendRegistrationPaymentUpdate = async (response) => {
        const payload = {
            booked_by: userData.name,
            country_id: userData.country_id,
            member_id: userData.member_id,
            online_payment_id: response.onlinePaymentId,
            order_id: response.razorpay_order_id,
            payment_status: "success",
            signature: response.razorpay_signature,
            state_id: userData.state_id,
            transaction_id: response.razorpay_payment_id,
            user_id: userData.id,
        };

        const res = await postData({
            url: `${config.serverUrl}/singupPaymentCheckout.php`,
            payload,
            highlightError: true,
        });

        setIsLoading(false);

        if (res) {
            setRegistrationPaymentStatus("");
            toast.success("Registration Update Successful! You can now book events.");
        }
    };

    const makeRegistrationPayment = async () => {
        setIsLoading(true);
        const payload = {
            mobile: registrationPaymentStatus.mobile,
            memberId: registrationPaymentStatus.memberId,
            name: registrationPaymentStatus.name,
            amount: registrationPaymentStatus.regAmount * 100,
            currency: registrationPaymentStatus.currency,
            email: registrationPaymentStatus.email,
            description: "Registration",
            apkRefNo: `${registrationPaymentStatus.memberId}${Number(new Date())}`,
            bookInfo: "Sign up Payment",
        };

        try {
            const res = await postData({
                url: `${config.serverUrl}/getRazorpayOrderId.php`,
                payload,
                highlightError: true,
            });

            if (res) {
                displayRazorpayPaymentSdk({
                    ...res,
                    setShowLoader: setIsLoading,
                    onPaymentSuccess: sendRegistrationPaymentUpdate,
                });
            } else {
                setIsLoading(false);
                toast.error("Something went wrong!");
            }
        } catch (error) {
            setIsLoading(false);
            toast.error("Something went wrong!");
            console.error(error);
        }
    };

    const submitRegistrationPayment = () => {
        makeRegistrationPayment();
    };

    // Enroll in Free Event
    const onEnrollEventClickHandler = async () => {
        setIsLoading(true);
        try {
            const payload = {
                booked_by: userData.name,
                country_id: userData.country_id,
                email: userData.email,
                event: {
                    booking_enddate: event.booking_enddate,
                    booking_endtime: event.booking_endtime,
                    event_amount: event.amount, // mapped to amount
                    event_for: event.event_for,
                    eventname: event.title, // mapped to title
                    id: event.id,
                    participant: event.participant,
                },
                member_id: userData.member_id,
                state_id: userData.state_id,
                user_id: userData.id,
            };

            const res = await postData({
                url: `${config.serverUrl}/eventFreeBooking.php`,
                payload,
                highlightError: true,
            });

            if (res) {
                toast.success("Enrolled successfully!");
                fetchEvents();
            }
        } catch (error) {
            console.error("Enrollment error", error);
            toast.error("Failed to enroll.");
        } finally {
            setIsLoading(false);
        }
    };

    // Join Zoom Meeting Event
    const postEventJoin = async () => {
        setIsLoading(true);
        try {
            const payload = {
                user_id: userData.id,
                event_id: event.id,
            };
            const joinDetails = await postData({
                url: `${config.serverUrl}/eventjoin.php`,
                payload,
                highlightError: true,
            });

            if (joinDetails) {
                // Navigate to meeting
                navigate("/meeting", {
                    state: {
                        meetingId: event.meeting_id,
                        meetingPass: event.meeting_pass,
                        zoomUserId: event.zoomUserId,
                        zoomSignature: event.zoomSignature,
                    },
                });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleJoinClick = () => {
        if (event.btnStatus === "0") {
            const msg = event.joinBtnMessage ?? `This event will start on ${event.start_date} ${event.start_time}`;
            toast.info(msg);
        } else {
            postEventJoin();
        }
    };

    // Razorpay Flow for Paid Events
    const sendEventPaymentUpdate = async (response) => {
        let payload = {
            booked_by: userData.name,
            country_id: userData.country_id,
            email: userData.email,
            event: {
                booking_enddate: event.booking_enddate,
                booking_endtime: event.booking_endtime,
                btnStatus: event.btnStatus,
                eventCancelReason: event.cancel_reason,
                eventStatus: event.eventStatus,
                event_amount: event.amount,
                event_for: event.event_for,
                event_time: event.start_time,
                event_type: event.event_type,
                eventdate: event.start_date,
                eventname: event.title,
                id: event.id,
                isCancel: event.is_cancelled,
                participant: event.participant,
            },
            member_id: userData.member_id,
            online_payment_id: response.onlinePaymentId,
            order_id: response.razorpay_order_id,
            payment_status: "success",
            signature: response.razorpay_signature,
            state_id: userData.state_id,
            transaction_id: response.razorpay_payment_id,
            user_id: userData.id,
        };

        const res = await postData({
            url: `${config.serverUrl}/eventBooking.php`,
            payload,
            highlightError: true,
        });

        setIsLoading(false);
        if (res) {
            toast.success("Booking Successful!");

            if (res.whatsapplink) {
                setPaymentUpdateResponse(res);
            } else {
                hideWhatsappDialog();
            }
        }
    };

    const onBookPaidEventClickHandler = async () => {
        setIsLoading(true);
        try {
            const payload = {
                mobile: userData.mobile || userData.phone || "",
                description: "Event Booking",
                userId: userData.member_id,
                currency: userData.currency || "INR",
                email: userData.email,
                apkRefNo: `${userData.member_id}${Number(new Date())}`,
                bookInfo: `${event.title} ${event.start_date}`,
                booked_by: userData.name,
                event_amount: Number(event.amount),
                event_for: event.event_for,
                eventname: event.title,
                eventid: event.id,
                participant: event.participant,
            };

            const fullData = await postData({
                url: `${config.serverUrl}/getEventBookOrderId.php`,
                payload,
                highlightError: true,
                getFullResponse: true,
            });

            console.log("Payment Initialization Response:", fullData);

            if (!fullData || fullData.status === "failed" || !fullData.response) {
                setIsLoading(false);
                // auth.js postData handles the toast via highlightError automatically.
                return;
            }

            const res = fullData.response;

            if (res && res.regStatus !== 0) {
                // Initialize Payment SDK Overlay
                displayRazorpayPaymentSdk({
                    ...res,
                    setShowLoader: setIsLoading,
                    onPaymentSuccess: sendEventPaymentUpdate,
                });
            } else {
                setIsLoading(false);
                if (res?.regStatus === 0) {
                    setRegistrationPaymentStatus(res);
                } else {
                    toast.error("Failed to initialize payment gateway.");
                }
            }
        } catch (error) {
            setIsLoading(false);
            toast.error("Something went wrong!");
            console.error("Booking error", error);
        }
    };

    const renderDialogs = () => (
        <>
            {paymentUpdateResponse?.whatsapplink ? (
                <WhatsappJoinDialog
                    show={Boolean(paymentUpdateResponse.whatsapplink)}
                    primaryMsg={paymentUpdateResponse.first}
                    secondaryMsg={paymentUpdateResponse.second}
                    whatsappLink={paymentUpdateResponse.whatsapplink}
                    onCloseHandler={hideWhatsappDialog}
                />
            ) : null}

            {registrationPaymentStatus && (
                <RegistrationPaymentDialog
                    onSubmit={submitRegistrationPayment}
                    state={registrationPaymentStatus}
                    onClose={() => setRegistrationPaymentStatus("")}
                />
            )}
        </>
    );

    // If event is cancelled no booking actions are available
    if (event.is_cancelled) {
        return null;
    }

    // Determine button text and action based on booking status
    if (event.booking_status === "open") {
        if (!event.is_paid) {
            return (
                <>
                    <button
                        className={actionButtonStyle("#64B443", "#559c38")}
                        onClick={onEnrollEventClickHandler}
                        disabled={isLoading}
                    >
                        Enroll Now {isLoading && <Loader show={true} />}
                    </button>
                    {renderDialogs()}
                </>
            );
        }
        return (
            <>
                <button
                    className={actionButtonStyle("#f5b940", "#dca332")}
                    onClick={onBookPaidEventClickHandler}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <>Processing... <Loader show={true} /></>
                    ) : (
                        "Pay Now"
                    )}
                </button>
                {renderDialogs()}
            </>
        );
    }

    // For non-open booking statuses, we now surface availability as part of
    // the metrics row in EventDetails instead of showing a disabled button.
    return null;
};

export default EventActionButton;
